import React from 'react';
import { useLocation } from 'react-router';
import {
  useOrgApproval,
  type OrganizationActivatedPayload,
  type OrganizationRejectedPayload,
} from '@redbax/shared';

type Phase = 'waiting' | 'approved' | 'rejected';

const statusLabel: Record<string, string> = {
  disconnected: 'Reconnecting…',
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  connected: 'Listening for approval…',
};

export function AwaitingApprovalPage() {
  const location = useLocation();
  const state = location.state as { orgName?: string; orgId?: string } | null;
  const orgName = state?.orgName ?? 'Your organization';
  const pendingOrgId = state?.orgId;

  const [phase, setPhase] = React.useState<Phase>('waiting');
  const [rejectionReason, setRejectionReason] = React.useState<string | null>(null);

  // Match an incoming event to this user's pending org. If we don't know the
  // orgId (no route state), the connection is already user-scoped so we react
  // to any activation/rejection.
  const matchesPending = React.useCallback(
    (eventOrgId: string) => !pendingOrgId || eventOrgId === pendingOrgId,
    [pendingOrgId],
  );

  const handleActivated = React.useCallback(
    (payload: OrganizationActivatedPayload) => {
      if (!matchesPending(payload.orgId)) return;
      setPhase('approved');
      // Navigate to the gated root route ('/'). This page lives OUTSIDE the
      // IdentityGate (it's a standalone pre-gate route), so a plain reload here
      // would just re-render this page. Hard-navigating to '/' mounts the gate,
      // which re-bootstraps, sees the now-Active org, setCurrentOrg, and renders
      // the app.
      window.setTimeout(() => window.location.assign('/'), 1200);
    },
    [matchesPending],
  );

  const handleRejected = React.useCallback(
    (payload: OrganizationRejectedPayload) => {
      if (!matchesPending(payload.orgId)) return;
      setRejectionReason(payload.reason);
      setPhase('rejected');
    },
    [matchesPending],
  );

  const { status } = useOrgApproval({
    enabled: phase === 'waiting',
    onActivated: handleActivated,
    onRejected: handleRejected,
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-8 text-center">
        {phase === 'approved' ? (
          <>
            <div className="mb-4 text-4xl">✅</div>
            <h1 className="mb-2 text-2xl font-semibold text-text-primary">Approved</h1>
            <p className="mb-1 text-sm font-medium text-text-primary">{orgName}</p>
            <p className="mb-6 text-sm text-text-secondary">
              Loading your workspace…
            </p>
          </>
        ) : phase === 'rejected' ? (
          <>
            <div className="mb-4 text-4xl">🚫</div>
            <h1 className="mb-2 text-2xl font-semibold text-text-primary">Request rejected</h1>
            <p className="mb-1 text-sm font-medium text-text-primary">{orgName}</p>
            <p className="mb-6 text-sm text-text-secondary">
              {rejectionReason
                ? rejectionReason
                : 'Your organization request was not approved. Please contact your administrator.'}
            </p>
          </>
        ) : (
          <>
            <div className="mb-4 text-4xl">🔒</div>
            <h1 className="mb-2 text-2xl font-semibold text-text-primary">Awaiting approval</h1>
            <p className="mb-1 text-sm font-medium text-text-primary">{orgName}</p>
            <p className="mb-6 text-sm text-text-secondary">
              Your organization has been created and is pending admin approval. You'll be able to
              access the dashboard once it's been approved and provisioned.
            </p>

            <div className="mb-6 flex items-center justify-center gap-2 text-xs text-text-secondary">
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  status === 'connected'
                    ? 'bg-green-500'
                    : status === 'connecting' || status === 'reconnecting'
                      ? 'bg-yellow-500'
                      : 'bg-text-secondary'
                }`}
              />
              {statusLabel[status] ?? 'Listening for approval…'}
            </div>

            <button
              onClick={() => window.location.assign('/')}
              className="w-full rounded-lg bg-red-accent px-4 py-2 text-sm font-medium text-text-primary hover:bg-red-hover"
            >
              Check again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
